import React, { useState, useEffect, useRef, useCallback } from 'react';
import Toolbar from './Toolbar';
import NoteViewMain from './MainPanel/NoteViewMain';
import styles from './MainPanel.module.css';
import DescriptionModal from './MainPanel/DescriptionModal';
import { usePanelContext, PANEL_COMPONENTS } from './contexts/PanelContext';
import { useWorkspaces } from "./contexts/WorkspacesContext";
import { useNotes } from "./contexts/NotesContext";  // ✅ ADD: For workspace notes
import StickysViewMain from './StickysViewMain';
import NoteEditorContainer from './MainPanel/NoteEditorContainer';
import NoteTitleBar from './NoteTitleBar';
import FormatBar from './FormatBar';
import ConfirmModal from './ConfirmModal';



const MainPanel = ({ 
  isImageUploadExpanded,
  activeDock,
  setActiveDock,
  isRightPanelVisible,
  isLeftPanelVisible,
  toggleRightPanel,
  toggleLeftPanel,
  onToggleImageUpload,
  onSidebarContentChange,
  activeContent,
  onContainerSwap,
  onOpenSettings,
  onImageUpload 
}) => {
  // Get workspaces context - handles documents and workspace operations
    const { 
  selectedDocumentPublicId,
  selectedWorkspacePublicId,  // ✅ ADD THIS
  deleteWorkspace,
  deleteDocument,
  createWorkspace,
  createDocument,             // ✅ ADD THIS
  updateDocument,
  workspaces,
  documents,
  selectDocument
} = useWorkspaces();
  
  // Get notes context - handles workspace-scoped notes (formerly stickies)
  const {
    notes,           // Workspace notes
    addNote,         // Create new note
    updateNote,      // Update note
    removeNote       // Delete note
  } = useNotes();

  const [uploadedImageId, setUploadedImageId] = useState(null);
  const [isDescriptionModalOpen, setDescriptionModalOpen] = useState(false);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const noteViewRef = useRef();
  const { mainPanelContent } = usePanelContext();
  const [isDeleting, setIsDeleting] = useState(false);
  const [arrangementMode, setArrangementMode] = useState('default');
  const [stickyFunctions, setStickyFunctions] = useState(null);
  const [isAIModalOpen, setIsAIModalOpen] = useState(false);
  const [selectedCount, setSelectedCount] = useState(0);


const handleStickyFunctionsReady = useCallback((functions) => {
  setStickyFunctions(functions);
  
  // Update count whenever sticky functions are ready
  if (functions.getSelectedCount) {
    setSelectedCount(functions.getSelectedCount());
  }
}, []);

const handleDeleteNote = async () => {
  setIsDeleting(true);
  try {
    const currentDoc = documents.find(d => d.public_id === selectedDocumentPublicId);
    
    if (currentDoc) {
      const workspaceDocs = documents.filter(d => 
        d.workspace_public_id === currentDoc.workspace_public_id &&
        d.public_id !== selectedDocumentPublicId
      );
      
      await deleteDocument(selectedDocumentPublicId);
      
      if (workspaceDocs.length > 0) {
        // Select the first remaining document in the same workspace
        selectDocument(workspaceDocs[0].public_id);
      } else {
        // ✅ FIX: No more documents in this workspace - clear selection
        selectDocument(null);
      }
    }
    
    setDeleteModalOpen(false);
  } catch (error) {
    console.error('Delete failed:', error);
  } finally {
    setIsDeleting(false);
  }
};


// Add these handler functions before the return statement
const handleOpenAIModal = useCallback(() => {
  if (stickyFunctions?.handleOpenAIModal) {
    stickyFunctions.handleOpenAIModal();
  }
}, [stickyFunctions]);

const handleFreezeLayout = useCallback(() => {
  if (stickyFunctions?.handleFreezeLayout) {
    stickyFunctions.handleFreezeLayout();
  }
}, [stickyFunctions]);

const handleAddSticky = useCallback(() => {
  if (stickyFunctions?.handleAddSticky) {
    stickyFunctions.handleAddSticky();
  }
}, [stickyFunctions]);

const handleCycleArrange = useCallback(() => {
  if (stickyFunctions?.cycleArrangements) {
    const nextMode = stickyFunctions.cycleArrangements();
    if (nextMode) {
      setArrangementMode(nextMode);
    }
  }
}, [stickyFunctions]);


const handleAddDocument = useCallback(async () => {
  if (!selectedWorkspacePublicId) {
    console.log('No workspace selected to add document');
    return;
  }
  
  try {
    const newDocument = await createDocument(selectedWorkspacePublicId);
    if (newDocument?.public_id) {
      selectDocument(newDocument.public_id);
    }
  } catch (error) {
    console.error('Error creating document:', error);
  }
}, [selectedWorkspacePublicId, createDocument, selectDocument]);


  const refreshGallery = async () => {
    console.log('Attempting gallery refresh...');
    if (noteViewRef.current && noteViewRef.current.refreshImages) {
      console.log('Calling refreshImages...');
      await noteViewRef.current.refreshImages();
      console.log('Gallery refresh complete');
    }
  };

  const handleImagePaste = async (e) => {
    // ... existing image paste handling
  };

  const handleDescriptionUpdate = async (description) => {
    // ... existing description update handling
  };


  // Add a polling effect to keep count updated (or trigger on actions)
useEffect(() => {
  if (stickyFunctions?.getSelectedCount) {
    const interval = setInterval(() => {
      setSelectedCount(stickyFunctions.getSelectedCount());
    }, 500); // Check every 500ms
    
    return () => clearInterval(interval);
  }
}, [stickyFunctions]);


  const renderDock = () => {
    const { mainPanelContent, isSwapped, PANEL_COMPONENTS } = usePanelContext();
    
    const noteViewProps = {
      ref: noteViewRef,
      isImageUploadExpanded: isImageUploadExpanded,
      onToggleImageUpload: onToggleImageUpload,
      onImagePaste: handleImagePaste,
      onImageUpload: onImageUpload
    };
    
    // When panels are swapped, adjust rendering behavior
    if (isSwapped) {
      // If main panel should show threads (which normally belong in right panel)
      
      // If main panel should show sticky notes
      if (mainPanelContent === PANEL_COMPONENTS.MAIN_PANEL.STICKY_NOTES) {
        return <StickysViewMain 
          selectedDocumentPublicId={selectedDocumentPublicId}   // ✅ RENAMED: was selectedNotePublicId
          onFunctionsReady={handleStickyFunctionsReady}
          arrangementMode={arrangementMode}
          notes={notes}                // ✅ RENAMED: was stickies
          updateNote={updateNote}      // ✅ RENAMED: was updateSticky
          removeNote={removeNote}      // ✅ RENAMED: was removeSticky
          addNote={addNote}            // ✅ RENAMED: was addSticky
          isInMainPanel={true}
          isRightPanelVisible={isRightPanelVisible}
          onRequestDelete={() => setDeleteModalOpen(true)} 
        />;
      }
    }
    
    // Default/normal panel content rendering
    switch (mainPanelContent) {
      case PANEL_COMPONENTS.MAIN_PANEL.NOTE_VIEW:
        return <NoteEditorContainer {...noteViewProps} />;
      case PANEL_COMPONENTS.MAIN_PANEL.STICKY_NOTES:
        return <StickysViewMain 
          selectedDocumentPublicId={selectedDocumentPublicId}   // ✅ RENAMED: was selectedNotePublicId
          onFunctionsReady={handleStickyFunctionsReady}
          arrangementMode={arrangementMode}
          notes={notes}                // ✅ RENAMED: was stickies
          updateNote={updateNote}      // ✅ RENAMED: was updateSticky
          removeNote={removeNote}      // ✅ RENAMED: was removeSticky
          addNote={addNote}            // ✅ RENAMED: was addSticky
          isInMainPanel={true} // Flag to indicate this is rendered in main panel
          isRightPanelVisible={isRightPanelVisible}
        />;

      default:
        return <NoteEditorContainer {...noteViewProps} />;
    }
  };

  // Compute class name based on panel visibility
  const mainPanelClasses = `${styles.mainPanel} ${
    isLeftPanelVisible ? styles.withLeftPanel : styles.withoutLeftPanel
  } ${
    isRightPanelVisible ? styles.withRightPanel : styles.withoutRightPanel
  }`;

  return (
    <div className={mainPanelClasses}>
      <Toolbar 
          isRightPanelVisible={isRightPanelVisible}
          isLeftPanelVisible={isLeftPanelVisible}
          toggleRightPanel={toggleRightPanel}
          toggleLeftPanel={toggleLeftPanel}
          onDeleteNote={() => setDeleteModalOpen(true)}
          onAddNote={createWorkspace}
          onAddDocument={handleAddDocument}  // ✅ ADD THIS LINE
          onToggleImageUpload={onToggleImageUpload}
          onSidebarContentChange={onSidebarContentChange}
          activeContent={activeContent}
          onContainerSwap={onContainerSwap}
          onOpenSettings={onOpenSettings}
          onAddSticky={handleAddSticky}
          onCycleArrange={handleCycleArrange}
          arrangementMode={arrangementMode}
          onOpenAIModal={handleOpenAIModal}
          onFreezeLayout={handleFreezeLayout}
          selectedStickyCount={selectedCount}
        />
      
      <NoteTitleBar />
      
      {mainPanelContent === PANEL_COMPONENTS.MAIN_PANEL.NOTE_VIEW && <FormatBar />}
      
      <div className={styles.mainDock}>
        <div className={`content-container ${styles.contentBg}`}>
          {renderDock()}
        </div>
      </div>
 
      <DescriptionModal
        isOpen={isDescriptionModalOpen}
        onClose={() => setDescriptionModalOpen(false)}
        onSave={handleDescriptionUpdate}
        defaultDescription="Untitled"
      />
 
<ConfirmModal
  isOpen={isDeleteModalOpen}
  onClose={() => setDeleteModalOpen(false)}
  onConfirm={handleDeleteNote}
  title="Delete Document?"
  message="This will permanently delete this document. The workspace and notes will remain."
  confirmText="Delete"
  cancelText="Cancel"
  type="danger"
  isProcessing={isDeleting}
/>
      

    </div>
  );
};

export default MainPanel;